/**
 * Realtime channel of the `/fleet` module (OneFleet ADR-003): one socket.io connection to `OneFleet-realtime`,
 * reached same-origin as `/fleet-rt` (Traefik strips the prefix; the socket.io path stays `/socket.io`, so the
 * client asks for `/fleet-rt/socket.io`). The OneSphere access token travels in the handshake `auth`; until
 * OneFleet WP-B2 verifies it the server answers every connection with `auth_required`, which the hook reports
 * as its own state rather than as an error. Screens join rooms per order, vehicle and driver from WP-B5 on, and
 * the events invalidate the `["fleet", …]` query keys.
 */
import { useEffect, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { getAccessToken } from "../frappe";
import { FLEET_RT } from "./client";

export const FLEET_SOCKET_PATH = `${FLEET_RT}/socket.io`;

export type FleetSocketState = "idle" | "connecting" | "connected" | "auth_required" | "disconnected" | "error";

let socket: Socket | null = null;
let state: FleetSocketState = "idle";
const listeners = new Set<(s: FleetSocketState) => void>();

function setState(s: FleetSocketState) {
  state = s;
  for (const l of listeners) l(s);
}

/** The module's socket, created on first use; `autoConnect` is off so nothing connects outside `/fleet/*`. */
export function fleetSocket(): Socket {
  if (!socket) {
    socket = io({
      path: FLEET_SOCKET_PATH,
      autoConnect: false,
      transports: ["websocket", "polling"],
      auth: (cb) => cb({ token: getAccessToken() }),
      reconnectionDelayMax: 10_000,
    });
    socket.on("connect", () => setState("connected"));
    socket.on("auth_required", () => setState("auth_required"));
    socket.on("disconnect", () => setState("disconnected"));
    socket.on("connect_error", () => setState("error"));
  }
  return socket;
}

export function connectFleetSocket(): Socket {
  const s = fleetSocket();
  if (!s.connected) {
    setState("connecting");
    s.connect();
  }
  return s;
}

export function disconnectFleetSocket(): void {
  if (socket?.connected || state === "connecting") socket?.disconnect();
  setState("idle");
}

/** The socket stays connected while at least one hook holds it (the status card, an event listener, a room
 * subscription — D-80: before, only the status card connected, so a board's listeners heard nothing unless the
 * ERP Hub had been opened); the last holder to leave closes it, so nothing stays connected outside `/fleet/*`. */
let holders = 0;

function useFleetConnection(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    holders += 1;
    connectFleetSocket();
    return () => {
      holders -= 1;
      if (holders <= 0) {
        holders = 0;
        disconnectFleetSocket();
      }
    };
  }, [active]);
}

/** Connection state for the shell's badge; holds the connection while mounted. */
export function useFleetSocketState(active = true): FleetSocketState {
  const [current, setCurrent] = useState<FleetSocketState>(state);
  useFleetConnection(active);
  useEffect(() => {
    if (!active) return;
    listeners.add(setCurrent);
    setCurrent(state);
    return () => {
      listeners.delete(setCurrent);
    };
  }, [active]);
  return current;
}

/**
 * WP-B5: a screen subscribes to record rooms (`vehicle:<id>`, `order:<id>`, `driver:`, `fleet:`, `zone:`,
 * `manifest:`) — the server joins only ids that exist in the socket's companies. Rooms are re-sent after every
 * reconnect; the last subscriber of a room leaves it.
 */
const roomRefs = new Map<string, number>();

function sendSubscribe(rooms: string[]) {
  const s = fleetSocket();
  if (rooms.length > 0 && s.connected) s.emit("subscribe", { rooms });
}

export function useFleetRooms(rooms: string[], active = true): void {
  const key = rooms.join("|");
  useFleetConnection(active && rooms.length > 0);
  useEffect(() => {
    if (!active || rooms.length === 0) return;
    const fresh = rooms.filter((r) => (roomRefs.get(r) ?? 0) === 0);
    for (const r of rooms) roomRefs.set(r, (roomRefs.get(r) ?? 0) + 1);
    sendSubscribe(fresh);
    const s = fleetSocket();
    const onConnect = () => sendSubscribe([...roomRefs.keys()]);
    s.on("connect", onConnect);
    return () => {
      s.off("connect", onConnect);
      const gone: string[] = [];
      for (const r of rooms) {
        const n = (roomRefs.get(r) ?? 1) - 1;
        if (n <= 0) {
          roomRefs.delete(r);
          gone.push(r);
        } else roomRefs.set(r, n);
      }
      if (gone.length > 0 && s.connected) s.emit("unsubscribe", { rooms: gone });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, active]);
}

/** Listen to one or more realtime events while mounted (the handler ref is read at call time); holds the connection. */
export function useFleetEvents(events: readonly string[], handler: (event: string, data: unknown) => void, active = true): void {
  const key = events.join("|");
  useFleetConnection(active);
  useEffect(() => {
    if (!active) return;
    const s = fleetSocket();
    const bound = events.map((ev) => [ev, (data: unknown) => handler(ev, data)] as const);
    for (const [ev, fn] of bound) s.on(ev, fn);
    return () => {
      for (const [ev, fn] of bound) s.off(ev, fn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, active, handler]);
}
